import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private http = inject(HttpClient);

  private formatErrors(error: any): Observable<never> {
    return throwError(() => error.error);
  }

  /**
   * @param  {string} path Path from API root, it should not begin with / 
   * @param  {any} [params] Query params
   * 
   * @returns {Observable<any>}
   */
  get(path: string, params: any = {}): Observable<any> {
    const token = localStorage.getItem(environment.accessTokenName);

    const headers: any = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    headers['Content-Type'] = 'application/json';

    return this.http
      .get(`${environment.apiRoot}${path}`, { params, headers })
      .pipe(catchError(this.formatErrors));
  }

  /**
   * @param  {string} path Path from API root, it should not begin with / 
   * @param  {any} [body] Body params
   * 
   * @returns {Observable<any>}
   */
  put(path: string, body: any = {}): Observable<any> {
    const token = localStorage.getItem(environment.accessTokenName);

    const headers: any = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    headers['Content-Type'] = 'application/json';

    return this.http
      .put(`${environment.apiRoot}${path}`, JSON.stringify(body), { headers })
      .pipe(catchError(this.formatErrors));
  }

  /**
   * @param  {string} path Path from API root, it should not begin with / 
   * @param  {any} [body]
   * 
   * @returns {Observable<any>}
   */
  post(path: string, body: any = {}): Observable<any> {
    const token = localStorage.getItem(environment.accessTokenName);

    const headers: any = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    headers['Content-Type'] = 'application/json';

    return this.http
      .post(`${environment.apiRoot}${path}`, JSON.stringify(body), { headers })
      .pipe(catchError(this.formatErrors));
  }

  /**
   * @param  {string} path Path from API root, it should not begin with / 
   * @param  {any} [params]
   * 
   * @returns {Observable<any>}
   */
  delete(path: string, params?: any): Observable<any> {
    const token = localStorage.getItem(environment.accessTokenName);

    const headers: any = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    headers['Content-Type'] = 'application/json';

    return this.http
      .delete(`${environment.apiRoot}${path}`, { params, headers })
      .pipe(catchError(this.formatErrors));
  }

  /**
   * @param  {string} path Path from API root, it should not begin with / 
   * @param  {any} [body]
   * @param  {any} [headers]
   * 
   * @returns {Observable<any>}
   */
  upload(path: string, body: any = {}, headers: any = {}): Observable<any> {
    const token = localStorage.getItem(environment.accessTokenName);
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Transform the body to form data
    const formData = new FormData();
    for (const key in body) {
      if (Object.prototype.hasOwnProperty.call(body, key)) {
        formData.append(key, body[key]);
      }
    }

    return this.http.post(`${environment.apiRoot}${path}`, formData, { headers })
      .pipe(catchError(this.formatErrors));
  }

  /**
   * @param  {string} path Path from API root, it should not begin with / 
   * @param  {string} filename File name with extension
   * @param  {any} [params]
   * 
   * @returns {Observable<any>}
   */
  download(path: string, filename: string, params?: any): Observable<any> {
    const token = localStorage.getItem(environment.accessTokenName);
    
    const headers: any = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.get(`${environment.apiRoot}${path}`, { params, headers, responseType: 'arraybuffer' })
      .pipe(
        map((response: any) => {
          const [, extension] = filename.split('.');
          let type = 'application/octet-stream';

          if (extension === 'xlsx') {
            type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          } else if (extension === 'xls') {
            type = 'application/vnd.ms-excel';
          } else if (extension === 'csv') {
            type = 'text/csv';
          } else if (extension === 'pdf') {
            type = 'application/pdf';
          } 

          const file = new Blob([response], { type });
          const fileURL = URL.createObjectURL(file);
          const a = document.createElement('a');
          a.href = fileURL;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }),
        catchError(this.formatErrors)
      );
  }
}
